import type { CountryDetail, MailingGroup } from "@/api/mailing-groups.service";
import { isBarRegion } from "@/api/mailing-groups.service";

/**
 * An all-bar selection always produces 2 mailing groups (Instructing solicitor /
 * Other), regardless of how many bar regions are selected.
 */
export const BAR_GROUP_LABELS = ["Instructing solicitor", "Other"] as const;

export type RegionSelectionKind = "empty" | "bar" | "standard" | "mixed";

/**
 * Classifies a set of selected regions. The server returns 400 when bar and
 * non-bar regions are mixed, so the client mirrors that rule to block early.
 */
export function classifyRegionSelection(
  selected: Pick<CountryDetail, "type">[],
  isBar: (region: Pick<CountryDetail, "type">) => boolean = isBarRegion,
): RegionSelectionKind {
  if (selected.length === 0) return "empty";
  const hasBar = selected.some(isBar);
  const hasStandard = selected.some(region => !isBar(region));
  if (hasBar && hasStandard) return "mixed";
  return hasBar ? "bar" : "standard";
}

/**
 * Live "mailing groups to be generated" count.
 *
 * Generate merges all selected regions into a single group, so the count does
 * not depend on the number of regions — only on the kind of selection:
 * - All-bar selection → 2 groups (Instructing solicitor / Other).
 * - All-standard selection → 1 group.
 * - Mixed or empty selection → 0 (not a valid submission).
 */
export function calculateMailingGroupCount(
  selected: Pick<CountryDetail, "type">[],
  isBar: (region: Pick<CountryDetail, "type">) => boolean = isBarRegion,
): number {
  const kind = classifyRegionSelection(selected, isBar);
  if (kind === "empty" || kind === "mixed") return 0;
  if (kind === "bar") return 2;
  return 1;
}

/**
 * Business Rule 5: a mailing group is editable/deletable only until its campaign
 * has started; once any campaign is scheduled/sending/sent the group is locked.
 * Mirrors the server guard (409 MAILING_GROUP_LOCKED) so the UI blocks up-front.
 */
export function isGroupLocked(group: MailingGroup): boolean {
  return group.campaign_started === true;
}

/**
 * Display label for a publication. Several publications share a name and year
 * and differ only in the publication's own edition text, so that text has to
 * ride along: "Green Ambassador/2027 Edition (Asia Pacific)". Groups created
 * before it was captured fall back to the bare name.
 */
export function publicationDisplayLabel(
  publicationName: string,
  publicationEdition?: string | null,
): string {
  return publicationEdition ? `${publicationName}/${publicationEdition}` : publicationName;
}
